<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ChevronDown, ChevronUp, ChevronsUpDown, Eye, Pencil, Trash2 } from 'lucide-vue-next'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import { billingModeLabel, employeeTypeLabel } from '../utils/format'
import { resolveMediaUrl } from '../utils/files'
import type { Employee, Option } from '../types/hr'

const { t } = useI18n()
const auth = useAuthStore()
const { listEmployees, deleteEmployee, listWorkplaceOptions } = useHrApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canCreate = computed(() => permissions.value.includes('employees.create'))
const canUpdate = computed(() => permissions.value.includes('employees.update'))
const canDelete = computed(() => permissions.value.includes('employees.delete'))

const PAGE_SIZE = 20
/** `/employees` has no sort parameters, so the list is sorted client-side and
 *  every matching row is fetched once (100 per request, capped). */
const EMPLOYEE_FETCH_MAX_PAGES = 50

type SortKey =
  | 'id'
  | 'name'
  | 'employee_code'
  | 'department_name'
  | 'job_title'
  | 'employee_type'
  | 'billing_mode'
  | 'hire_date'
  | 'is_active'

type SortableKey = Exclude<SortKey, 'id'>

const allRows = ref<Employee[]>([])
const departments = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')

const filters = ref({
  search: '',
  department_agent_id: '' as string | number,
  employee_type: '',
  is_active: '1' as string | number,
})

const page = ref(1)
const activeTotal = ref(0)

/** Default matches the API order (`id DESC`) so the first render is unchanged. */
const sort = ref<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'id', dir: 'desc' })

const columns = computed<{ key: SortableKey; label: string }[]>(() => [
  { key: 'name', label: t('hr.employees.name') },
  { key: 'employee_code', label: t('hr.employees.code') },
  { key: 'department_name', label: t('hr.employees.department') },
  { key: 'job_title', label: t('hr.employees.jobTitle') },
  { key: 'employee_type', label: t('hr.employees.type') },
  { key: 'billing_mode', label: t('hr.employees.billing') },
  { key: 'hire_date', label: t('hr.employees.hireDate') },
  { key: 'is_active', label: t('hr.employees.status') },
])

function sortValue(employee: Employee, key: SortKey): string | number {
  switch (key) {
    case 'id':
      return employee.id
    case 'name':
      return `${employee.last_name ?? ''} ${employee.first_name ?? ''}`.trim().toLowerCase()
    case 'employee_code':
      return (employee.employee_code ?? '').toLowerCase()
    case 'department_name':
      return (employee.department_name ?? '').toLowerCase()
    case 'job_title':
      return (employee.job_title ?? '').toLowerCase()
    case 'employee_type':
      return employee.employee_type ?? ''
    case 'billing_mode':
      return employee.billing_mode ?? ''
    case 'hire_date':
      return employee.hire_date ?? ''
    case 'is_active':
      return employee.is_active ?? 0
  }
}

const sortedRows = computed(() => {
  const { key, dir } = sort.value
  const factor = dir === 'asc' ? 1 : -1
  return [...allRows.value].sort((a, b) => {
    const left = sortValue(a, key)
    const right = sortValue(b, key)
    const delta =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
    return delta * factor
  })
})

const total = computed(() => allRows.value.length)
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)))
const rows = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return sortedRows.value.slice(start, start + PAGE_SIZE)
})

let searchTimer: ReturnType<typeof setTimeout> | undefined

function ariaSort(key: SortableKey): 'ascending' | 'descending' | 'none' {
  if (sort.value.key !== key) return 'none'
  return sort.value.dir === 'asc' ? 'ascending' : 'descending'
}

function toggleSort(key: SortableKey) {
  sort.value =
    sort.value.key === key
      ? { key, dir: sort.value.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }
  page.value = 1
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params = {
      search: filters.value.search.trim() || undefined,
      department_agent_id: filters.value.department_agent_id || undefined,
      employee_type: filters.value.employee_type || undefined,
      is_active: filters.value.is_active,
    }
    const items: Employee[] = []
    let nextPage = 1
    let lastPage = 1
    do {
      const result = await listEmployees({ ...params, page: nextPage, per_page: 100 })
      items.push(...result.items)
      lastPage = Math.max(1, result.meta.total_pages)
      nextPage += 1
    } while (nextPage <= lastPage && nextPage <= EMPLOYEE_FETCH_MAX_PAGES)
    allRows.value = items
    page.value = Math.min(Math.max(1, page.value), Math.max(1, Math.ceil(items.length / PAGE_SIZE)))
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadSummary() {
  try {
    const active = await listEmployees({ is_active: 1, page: 1, per_page: 1 })
    activeTotal.value = active.meta.total
  } catch {
    activeTotal.value = 0
  }
}

async function loadDepartments() {
  try {
    departments.value = await listWorkplaceOptions()
  } catch {
    departments.value = []
  }
}

onMounted(() => {
  void loadDepartments()
  void loadSummary()
  void load()
})

watch(
  () => [filters.value.department_agent_id, filters.value.employee_type, filters.value.is_active],
  () => {
    page.value = 1
    void load()
  },
)

watch(
  () => filters.value.search,
  () => {
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
      page.value = 1
      void load()
    }, 300)
  },
)

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
}

async function remove(employee: Employee) {
  actionError.value = ''
  if (!window.confirm(t('hr.employees.deleteConfirm'))) return
  try {
    await deleteEmployee(employee.id)
    if (rows.value.length === 1 && page.value > 1) page.value -= 1
    await load()
    await loadSummary()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  }
}

function employeeName(employee: Employee): string {
  const name = `${employee.last_name} ${employee.first_name}`.trim()
  return name || employee.employee_code
}

/** Initials shown in the avatar when the employee has no photo. */
function initials(employee: Employee): string {
  const source = `${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim()
  const letters = source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
  return letters || employee.employee_code.slice(0, 2).toUpperCase()
}
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('hr.employees.title') }}</h2>
  </div>

  <div class="toolbar">
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('hr.common.searchPlaceholder')"
    >
    <select v-model="filters.department_agent_id" class="filter-select">
      <option value="">{{ t('hr.common.allDepartments') }}</option>
      <option v-for="department in departments" :key="department.id" :value="department.id">
        {{ department.name }}
      </option>
    </select>
    <select v-model="filters.employee_type" class="filter-select">
      <option value="">{{ t('hr.common.allTypes') }}</option>
      <option value="office">{{ t('hr.employeeType.office') }}</option>
      <option value="production">{{ t('hr.employeeType.production') }}</option>
      <option value="mixed">{{ t('hr.employeeType.mixed') }}</option>
    </select>
    <select v-model="filters.is_active" class="filter-select">
      <option value="1">{{ t('hr.common.active') }}</option>
      <option value="0">{{ t('hr.common.inactive') }}</option>
      <option value="">{{ t('hr.common.all') }}</option>
    </select>
    <span class="spacer" />
    <div class="summary">
      <span>{{ t('hr.employees.totals', { total, active: activeTotal }) }}</span>
    </div>
    <RouterLink v-if="canCreate" class="btn-primary" to="/employees/new">
      {{ t('hr.employees.newEmployee') }}
    </RouterLink>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('hr.employees.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
  </div>

  <div v-else-if="rows.length === 0" class="state-message"><p>{{ t('hr.employees.empty') }}</p></div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              class="is-sortable"
              :aria-sort="ariaSort(column.key)"
            >
              <button
                class="sort-header"
                :class="{ 'is-active': sort.key === column.key }"
                type="button"
                :title="column.label"
                @click="toggleSort(column.key)"
              >
                {{ column.label }}
                <ChevronUp
                  v-if="sort.key === column.key && sort.dir === 'asc'"
                  :size="13"
                  aria-hidden="true"
                />
                <ChevronDown
                  v-else-if="sort.key === column.key && sort.dir === 'desc'"
                  :size="13"
                  aria-hidden="true"
                />
                <ChevronsUpDown v-else :size="13" aria-hidden="true" />
              </button>
            </th>
            <th class="cell-actions">{{ t('hr.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employee in rows" :key="employee.id">
            <td class="cell-name">
              <span class="cell-person">
                <img
                  v-if="employee.photo_url"
                  class="employee-avatar"
                  :src="resolveMediaUrl(employee.photo_url)"
                  :alt="employeeName(employee)"
                  loading="lazy"
                >
                <span v-else class="employee-avatar employee-avatar--initials" aria-hidden="true">
                  {{ initials(employee) }}
                </span>
                <RouterLink class="cell-person__name" :to="`/employees/${employee.id}`">
                  {{ employeeName(employee) }}
                </RouterLink>
              </span>
            </td>
            <td class="cell-code">{{ employee.employee_code }}</td>
            <td>{{ employee.department_name || '—' }}</td>
            <td>{{ employee.job_title || '—' }}</td>
            <td>{{ employeeTypeLabel(t, employee.employee_type) }}</td>
            <td>{{ billingModeLabel(t, employee.billing_mode) }}</td>
            <td class="cell-date">{{ formatDisplayDate(employee.hire_date) || '—' }}</td>
            <td>
              <span :class="['status-badge', employee.is_active === 1 ? 'status-ok' : 'status-neutral']">
                {{ employee.is_active === 1 ? t('hr.common.active') : t('hr.common.inactive') }}
              </span>
            </td>
            <td class="cell-actions">
              <RouterLink
                class="icon-action"
                :to="`/employees/${employee.id}`"
                :title="t('hr.employees.view')"
                :aria-label="t('hr.employees.view')"
              >
                <Eye :size="16" aria-hidden="true" />
              </RouterLink>
              <RouterLink
                v-if="canUpdate"
                class="icon-action"
                :to="`/employees/${employee.id}/edit`"
                :title="t('hr.employees.edit')"
                :aria-label="t('hr.employees.edit')"
              >
                <Pencil :size="16" aria-hidden="true" />
              </RouterLink>
              <button
                v-if="canDelete"
                class="icon-action danger"
                type="button"
                :title="t('hr.employees.delete')"
                :aria-label="t('hr.employees.delete')"
                @click="remove(employee)"
              >
                <Trash2 :size="16" aria-hidden="true" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ t('hr.common.page', { page, total: Math.max(totalPages, 1) }) }}</span>
      <button type="button" :disabled="page <= 1" @click="goToPage(page - 1)">
        {{ t('hr.common.previous') }}
      </button>
      <button type="button" :disabled="page >= totalPages" @click="goToPage(page + 1)">
        {{ t('hr.common.next') }}
      </button>
    </div>
  </template>
</template>
